import {
  Button,
  cn,
  Combobox,
  type ComboboxOption,
  DatePicker,
  Input,
  NumberInput,
  StringSelect,
} from "@tellescope/ui";
import { X } from "lucide-react";
import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getAllFreeformOperations,
  DATE_OPERATIONS,
  NO_VALUE_OPERATIONS,
  NUMERIC_OPERATIONS,
  STRING_OPERATIONS,
  type FreeformFieldOption,
  type FreeformFilter,
} from "./freeformFilters";

export interface FreeformFilterRowProps {
  filter: FreeformFilter;
  fieldOptions: FreeformFieldOption[];
  onChange: (filter: FreeformFilter) => void;
  onRemove: () => void;
  variant?: "and" | "or";
}

export const FreeformFilterRow = ({
  filter,
  fieldOptions,
  onChange,
  onRemove,
  variant = "and",
}: FreeformFilterRowProps) => {
  const { t } = useTranslation();
  const id = useId();

  const selectedField = fieldOptions.find((f) => f.value === filter.field);
  const fieldType = selectedField?.valueType ?? "string";

  const availableOperations = useMemo(() => {
    return getAllFreeformOperations().filter(({ value }) => {
      if (NO_VALUE_OPERATIONS.includes(value)) return true;
      if (NUMERIC_OPERATIONS.includes(value)) return fieldType === "number" || fieldType === "date";
      if (DATE_OPERATIONS.includes(value)) return fieldType === "date" || fieldType === "number";
      if (STRING_OPERATIONS.includes(value)) return fieldType === "string";
      return true;
    });
  }, [fieldType]);

  const showValueInput = !NO_VALUE_OPERATIONS.includes(filter.operation);
  const isDateOperation = DATE_OPERATIONS.includes(filter.operation);
  const isNumericOperation = NUMERIC_OPERATIONS.includes(filter.operation);

  const fieldComboboxOptions = useMemo<ComboboxOption[]>(
    () => fieldOptions.map((f) => ({ value: f.value, label: f.label })),
    [fieldOptions]
  );

  const selectedFieldOption = fieldComboboxOptions.find((o) => o.value === filter.field);

  return (
    <div
      className={cn(
        "grid grid-cols-[1fr_1fr_1fr_auto] gap-2 rounded-md border bg-gray-50 px-4 py-2 items-center",
        variant === "or" && "border-orange-300"
      )}
    >
      <Combobox
        value={selectedFieldOption}
        onChange={(selected) => {
          if (selected) {
            onChange({ field: selected.value, operation: "equals", value: undefined });
          }
        }}
        options={fieldComboboxOptions}
        placeholder={t("Select field")}
        searchPlaceholder={t("Search fields...")}
        className="text-xs font-light"
        popoverClassName="w-56"
        formatSelectedValue={(value) => <span className="font-light">{value.label}</span>}
        placeholderClassName="text-xs"
      />
      <StringSelect
        value={filter.operation}
        onValueChange={(operation) => onChange({ ...filter, operation, value: undefined })}
        options={availableOperations}
        placeholder={t("Operation")}
        className="bg-white text-xs font-light"
      />
      {showValueInput && (
        <>
          {isDateOperation && (
            <DatePicker
              key={`${id}-date`}
              value={filter.value !== undefined ? new Date(Number(filter.value)) : undefined}
              onChange={(date) => onChange({ ...filter, value: date?.getTime() })}
              placeholder={t("Date")}
            />
          )}
          {isNumericOperation && !isDateOperation && (
            <NumberInput
              key={`${id}-number`}
              value={typeof filter.value === "number" ? filter.value : undefined}
              onChange={(val) => onChange({ ...filter, value: val })}
              placeholder={t("Value")}
              className="w-28"
            />
          )}
          {!isDateOperation && !isNumericOperation && (
            <Input
              key={`${id}-text`}
              value={typeof filter.value === "string" ? filter.value : ""}
              onChange={(e) => onChange({ ...filter, value: e.target.value })}
              placeholder={t("Value")}
              className="text-xs md:text-xs"
              size="small"
            />
          )}
        </>
      )}
      <Button
        variant="ghost"
        size="sm-icon"
        className="text-muted-foreground"
        onClick={onRemove}
        aria-label={t("Remove filter")}
      >
        <X size={4} />
      </Button>
    </div>
  );
};
